import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { createProfileSchema } from '@/schemas/profileSchema.ts';
import type { ProfileFormValues } from '@/schemas/profileSchema.ts';
import { useAuth } from '@/context/AuthContext.tsx';
import { useAlert } from '@/context/AlertContext.tsx';
import authService from '@/services/authService.ts';
import FormError from '@/components/shared/FormError.tsx';

export default function ProfileInfo() {
  const { t } = useTranslation();
  const { user, refreshUser } = useAuth();
  const { showAlert } = useAlert();
  const [submitError, setSubmitError] = useState<string | null>(null);
  const schema = useMemo(() => createProfileSchema(t), [t]);

  function getRoleBadge(role: string) {
    if (role === 'SuperUser') {
      return (
        <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium bg-red-100 text-red-700">
          {t('common.roleSuperUser')}
        </span>
      );
    }
    const isManager = role === 'SalesManager';
    return (
      <span
        className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
          isManager
            ? 'bg-purple-100 text-purple-700'
            : 'bg-blue-100 text-blue-700'
        }`}
      >
        {isManager ? t('common.roleSalesManager') : t('common.roleSalesperson')}
      </span>
    );
  }

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<ProfileFormValues>({
    resolver: zodResolver(schema),
  });

  useEffect(() => {
    if (user) {
      reset({
        firstName: user.firstName,
        lastName: user.lastName,
        phone: user.phone ?? '',
      });
    }
  }, [user, reset]);

  const onSubmit = async (data: ProfileFormValues) => {
    setSubmitError(null);
    try {
      await authService.updateProfile({
        firstName: data.firstName,
        lastName: data.lastName,
        phone: data.phone || undefined,
      });
      await refreshUser();
      showAlert(t('profile.profileUpdated'), 'success');
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : t('profile.profileUpdateError'));
    }
  };

  return (
    <div className="rounded-xl border border-border bg-card p-6">
      <div className="mb-6 flex items-center gap-3">
        <h2 className="text-xl font-bold text-foreground">
          {t('profile.personalInfo')}
        </h2>
        {user && getRoleBadge(user.role)}
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
        {/* Name + Last name */}
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
          <div>
            <label
              htmlFor="profileFirstName"
              className="mb-1.5 block text-sm font-medium text-foreground"
            >
              {t('auth.firstNameLabel')}
            </label>
            <input
              id="profileFirstName"
              type="text"
              className="w-full rounded-lg border border-border bg-card px-3.5 py-2.5 text-sm text-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 transition-colors"
              {...register('firstName')}
            />
            {errors.firstName && (
              <p className="mt-1 text-xs text-destructive">
                {errors.firstName.message}
              </p>
            )}
          </div>

          <div>
            <label
              htmlFor="profileLastName"
              className="mb-1.5 block text-sm font-medium text-foreground"
            >
              {t('auth.lastNameLabel')}
            </label>
            <input
              id="profileLastName"
              type="text"
              className="w-full rounded-lg border border-border bg-card px-3.5 py-2.5 text-sm text-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 transition-colors"
              {...register('lastName')}
            />
            {errors.lastName && (
              <p className="mt-1 text-xs text-destructive">
                {errors.lastName.message}
              </p>
            )}
          </div>
        </div>

        {/* Email (read-only, distinct style) */}
        <div>
          <label
            htmlFor="profileEmail"
            className="mb-1.5 block text-sm font-medium text-muted-foreground"
          >
            {t('auth.emailLabel')}
          </label>
          <input
            id="profileEmail"
            type="email"
            value={user?.email ?? ''}
            readOnly
            className="w-full rounded-lg border border-border/50 bg-muted px-3.5 py-2.5 text-sm text-muted-foreground cursor-not-allowed"
          />
          <p className="mt-1 text-[11px] text-muted-foreground/60">
            {t('profile.emailReadonlyHint')}
          </p>
        </div>

        {/* Phone + Company */}
        <div className={`grid grid-cols-1 gap-5 ${user?.company ? 'sm:grid-cols-2' : ''}`}>
          <div>
            <label
              htmlFor="profilePhone"
              className="mb-1.5 block text-sm font-medium text-foreground"
            >
              {t('auth.phoneLabel')}
            </label>
            <input
              id="profilePhone"
              type="tel"
              placeholder={t('auth.phonePlaceholder')}
              className="w-full rounded-lg border border-border bg-card px-3.5 py-2.5 text-sm text-foreground placeholder:text-muted-foreground/50 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 transition-colors"
              {...register('phone')}
            />
            {errors.phone && (
              <p className="mt-1 text-xs text-destructive">
                {errors.phone.message}
              </p>
            )}
          </div>

          {/* Company: read-only for all users, only shown if set */}
          {user?.company && (
            <div>
              <label
                htmlFor="profileCompany"
                className="mb-1.5 block text-sm font-medium text-muted-foreground"
              >
                {t('auth.companyLabel')}
              </label>
              <input
                id="profileCompany"
                type="text"
                value={user.company}
                readOnly
                className="w-full rounded-lg border border-border/50 bg-muted px-3.5 py-2.5 text-sm text-muted-foreground cursor-not-allowed"
              />
            </div>
          )}
        </div>

        <FormError
          message={submitError}
          onDismiss={() => setSubmitError(null)}
        />

        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? t('common.saving') : t('common.saveChanges')}
        </Button>
      </form>
    </div>
  );
}
